import {
  getAnovaGetRunQueryKey,
  useAnovaBatch,
  useAnovaGetRun,
  useAnovaPublish,
  useAnovaRun,
  useAnovaRuns,
} from "@api/generated/anova/anova";
import type { AnovaIn } from "@api/generated/model";
import { useUserMe } from "@api/generated/user/user";
import { hasRole, useSeasonFromLink } from "@app/workspace";
import { AnovaBatch } from "@features/statistics/AnovaBatch";
import { AnovaForm } from "@features/statistics/AnovaForm";
import { AnovaResult } from "@features/statistics/AnovaResult";
import { useWatchedJob } from "@features/system/jobs";
import { AnimatedTabsList } from "@ui/AnimatedTabsList";
import { QueryFeedback } from "@ui/QueryFeedback";
import "@features/statistics/statistics.css";
import { Loader, Menu, Tabs } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { ConfirmModal } from "@shell/Dialogs";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { shortTime } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const KINDS = ["rcbd", "sites", "years", "split"] as const;
type Kind = (typeof KINDS)[number];

export const Route = createFileRoute("/analysis/anova")({
  validateSearch: z.object({ ev: z.number().int().optional(), tab: z.enum(KINDS).optional(), run: z.string().optional() }),
  component: AnovaPage,
});

/** Elemzés és nyomtatás → Varianciaanalízis (the legacy ANOVA 1 / ANOVA 2): one trial in blocks, entries × sites or
 *  × years (blocks nested in their environment), a split plot. A run is stored: the link names it (``?run=``), it is
 *  opened again with its settings, repeated, exported as a protocol and, for one factor, published into the trial
 *  data. One factor and entries × sites also run as a batch over many trials (a background job, shown here until a
 *  run is opened). */
function AnovaPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const season = useSeasonFromLink(search.ev);
  const me = useUserMe().data;
  const client = useQueryClient();
  const runQuery = useAnovaGetRun(search.run ?? "", { query: { enabled: !!search.run } });
  const run = runQuery.data;
  const tab: Kind = search.tab ?? (run?.request.kind as Kind | undefined) ?? "rcbd";
  const runs = useAnovaRuns({ limit: 30 }).data ?? [];
  const tabRuns = runs.filter((r) => r.kind === tab);
  const [confirming, setConfirming] = useState(false);
  // a run of another year switches the working season to it (the link carries ?ev=)
  const open = (id: string, kind?: Kind, ev?: number) =>
    void navigate({ search: (s) => ({ ...s, run: id, tab: kind ?? s.tab, ev: ev ?? s.ev }) });
  const start = useAnovaRun({
    mutation: {
      onSuccess: (out) => {
        client.setQueryData(getAnovaGetRunQueryKey(out.id), out);
        void client.invalidateQueries({ queryKey: ["/api/v1/anova/runs"] });
        open(out.id);
      },
    },
  });
  const publish = useAnovaPublish({
    mutation: {
      onSuccess: (out) => {
        setConfirming(false);
        notifications.show({ color: "green", message: t("anova.publishedCount", { count: out.plots }) });
        if (run) client.setQueryData(getAnovaGetRunQueryKey(run.id), { ...run, published_at: new Date().toISOString() });
        void client.invalidateQueries({ queryKey: ["/api/v1/anova/runs"] });
      },
    },
  });
  const watched = useWatchedJob();
  const [batchTab, setBatchTab] = useState<Kind | null>(null);
  const batch = useAnovaBatch({
    mutation: {
      onSuccess: (out) => {
        watched.watch(out.job_id);
        setBatchTab(tab);
        void navigate({ search: (s) => ({ ...s, run: undefined }) });
      },
    },
  });
  const jobStatus = watched.job?.status;
  useEffect(() => {
    if (jobStatus === "done") void client.invalidateQueries({ queryKey: ["/api/v1/anova/runs"] });
  }, [jobStatus, client]);
  const showBatch = !!watched.jobId && batchTab === tab && !search.run;
  const initial = run && run.request.kind === tab ? (run.request as AnovaIn) : undefined;
  return (
    <div className="page fill">
      <PageHeader
        title={t("anova.title")}
        subtitle={t("anova.subtitle")}
        season
        actions={
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <button type="button" className="chip">
                {t("anova.previousRuns", { count: runs.length })}
              </button>
            </Menu.Target>
            <Menu.Dropdown mah={420} style={{ overflowY: "auto" }}>
              {runs.map((r) => (
                <Menu.Item key={r.id} onClick={() => open(r.id, r.kind as Kind)}>
                  {t(`anova.tabs.${r.kind}`)} · {r.title} · {shortTime(r.created_at)}
                  {r.published_at ? ` · ${t("anova.published")}` : ""}
                </Menu.Item>
              ))}
              {!runs.length && <Menu.Label>{t("anova.noRuns")}</Menu.Label>}
            </Menu.Dropdown>
          </Menu>
        }
      />
      <Tabs value={tab} onChange={(v) => v && void navigate({ search: (s) => ({ ...s, tab: v as Kind, run: undefined }) })}>
        <AnimatedTabsList mb="sm">
          {KINDS.map((k) => (
            <Tabs.Tab key={k} value={k}>
              {t(`anova.tabs.${k}`)}
            </Tabs.Tab>
          ))}
        </AnimatedTabsList>
      </Tabs>
      <QueryFeedback
        busy={start.isPending || runQuery.isFetching}
        error={runQuery.error}
        retry={runQuery.refetch}
        label={t("async.calculating")}
      />
      <div className="fill-row" style={{ alignItems: "flex-start" }}>
        {season != null && (
          <AnovaForm
            key={`${tab}-${initial ? run?.id : ""}`}
            kind={tab}
            season={season}
            initial={initial}
            running={start.isPending || batch.isPending}
            onRun={(data) => start.mutate({ data })}
            onBatch={(data) => batch.mutate({ data })}
            canPublish={hasRole(me?.roles, "nemesito")}
          />
        )}
        {showBatch ? (
          <AnovaBatch watched={watched} kind={tab} />
        ) : search.run && !run && !runQuery.isError ? (
          <Loader size="sm" m="md" />
        ) : runQuery.isError ? null : run && run.request.kind === tab ? (
          <AnovaResult
            run={run}
            canPublish={hasRole(me?.roles, "nemesito")}
            publishing={publish.isPending}
            onPublish={() => setConfirming(true)}
          />
        ) : (
          <section className="card pad grow stack" style={{ gap: 10 }}>
            <p className="sm mu">{t(`anova.empty.${tab}`)}</p>
            {tabRuns.length > 0 && (
              <>
                <span className="sec">{t("anova.previousRunsTitle")}</span>
                <div className="stack" style={{ gap: 4 }}>
                  {tabRuns.slice(0, 8).map((r) => (
                    <Link key={r.id} className="row-link" to="/analysis/anova" search={(s) => ({ ...s, run: r.id, tab })}>
                      <span className="grow stack">
                        <b>{r.title}</b>
                        <span className="sm mu">{shortTime(r.created_at)}</span>
                      </span>
                      {r.published_at && <span className="chip g">{t("anova.published")}</span>}
                    </Link>
                  ))}
                </div>
              </>
            )}
          </section>
        )}
      </div>
      {confirming && run && (
        <ConfirmModal
          title={t("anova.publishMenu")}
          text={t("anova.publishConfirm", { title: run.title, count: run.entries.length })}
          confirm={t("anova.publish")}
          loading={publish.isPending}
          onConfirm={() => publish.mutate({ id: run.id })}
          onClose={() => setConfirming(false)}
        />
      )}
    </div>
  );
}
